import { UseNavigateResult } from "@tanstack/react-router";
import { ZitadelConfig, createZitadelAuth } from "@zitadel/react";
import { ColumnHeader, mkConfig } from "export-to-csv";
import { orderBy, startCase, uniqBy } from "lodash-es";
import { User } from "oidc-client-ts";
import seedrandom from "seedrandom";

import { ACCESS_QUERY_KEY } from "~/access/queryKeys";
import {
  RESET,
  orgsAtom,
  selectedAccessRoleIdAtom,
  store,
  userAtom,
  userConfigAtom,
  workosAuthAtom,
  workosAuthInitialState,
  zitadelOrgAtom,
} from "~/atoms";
import type { MeqColumnDef, MeqRowData } from "~/components/MeqTable/types";
import i18n from "~/i18n";
import {
  isWorkOSAuth,
  redirectToAuthkitLogin,
  redirectToAuthkitLogout,
} from "~/lib/authProvider";
import { postLogout } from "~/lib/authkitClient";
import { queryClient } from "~/lib/queryClient";
import { Env } from "~/utils/Env";
import { getColumnLabelFallback } from "~/utils/columns";
import dayjs from "~/utils/dayjs";

import {
  ALL_LOCATIONS,
  CHART_DATA_COUNT_THRESHHOLD,
  MAIN_ENTRY_PAGE,
  NO_ORG_LOCATION,
  PERCENTAGE_OF,
  UPPER_CASE_TERMS,
  WORDS_EXCLUDING_FROM_PAGE_TITLE,
} from "./Constants";
import {
  Country,
  DateFormat,
  DateFormatServer,
  DateFormatUSA,
  GridPricingDataType,
  JsDataType,
  LooseObject,
  Org,
  QueryResult,
  QueryResultColumn,
  QueryResultTable,
  ZitadelAuth,
  ZitadelOrg,
} from "./Types";

export const delay = (ms: number) => new Promise((res) => setTimeout(res, ms));

export const isNotEmpty = (data: unknown) =>
  data !== undefined &&
  data !== null &&
  data !== "" &&
  JSON.stringify(data) !== "{}" &&
  JSON.stringify(data) !== "()" &&
  JSON.stringify(data) !== '"()"';

export const isStringEqualIgnoreCase = (str1: string, str2: string) =>
  str1 && str2 && str1.toLowerCase() === str2.toLowerCase() ? true : false;

export const isNumber = (
  value: unknown,
  acceptScientificNotation?: boolean,
) => {
  if (!acceptScientificNotation) {
    return /^-{0,1}\d+(\.\d+)?$/.test(String(value));
  }
  if (Array.isArray(value)) {
    return false;
  }
  return !isNaN(parseInt(String(value), 10));
};

export const isEvenNumber = (n: number): boolean => n % 2 === 0;

export const avoidJsFloatingPointPrecisionIssue = (num: number) =>
  Math.round((num + Number.EPSILON) * 10000) / 10000;

export const formatNumber = (number: number, precision: number): number => {
  const fixedNumber = number.toFixed(precision);
  return parseFloat(fixedNumber);
};

export const formatNumberWithCommas = (
  value: number | string | null | undefined,
  decimals = 0,
): string => {
  if (value == null || value === "") return "";
  const num = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(num)) return "";
  return num.toLocaleString("en-AU", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

export const getMaxDecimalsOfNumberArray = (arr: number[]) =>
  Math.max(...arr.map((num) => (num.toString().split(".")[1] || "").length));

export const getRandomInt = (min: number, max: number) => {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min)) + min; //The maximum is exclusive and the minimum is inclusive
};

export const getRandomString = (length = 18) => {
  const sample = "23456789ABCDEFGHIJKLMNPQRSTUVWXYZabcdefghijklmnpqrstuvwxyz";
  return Array.from(crypto.getRandomValues(new Uint32Array(length)))
    .map((x) => sample[x % sample.length])
    .join("");
};

type actAfterLoginSuccessProps = {
  orgs: Org[];
  user?: User;
  userConfig?: LooseObject;
};
export const actAfterLoginSuccess = ({
  orgs,
  user,
  userConfig,
}: actAfterLoginSuccessProps) => {
  if (orgs) {
    store.set(orgsAtom, orgs);
  }

  if (user) {
    store.set(userAtom, user);
  }

  if (userConfig) {
    store.set(userConfigAtom, userConfig);
  }
};

export const saveUserConfig = (userConfig: LooseObject) => {
  store.set(userConfigAtom, userConfig);
};

export const navigateIntoAppIfSingleOrg = (
  orgs: Org[],
  navigate: UseNavigateResult<string>,
  returnTo?: string,
) => {
  if (orgs && orgs.filter((o) => o?.isCurrent).length === 1) {
    if (returnTo) {
      void navigate({ href: returnTo });
    } else {
      void navigate({ to: MAIN_ENTRY_PAGE });
    }
  }
};

const getZitadelRedirectUri = (reactRedirectUri: string) => {
  let redirectUri = reactRedirectUri || "https://www.meqinsights.com";
  if (
    window.location.hostname === "localhost" &&
    window.location.port === "4173"
  ) {
    redirectUri = "http://localhost:4173";
  }
  if (Env.VITE_ENV === "development") {
    redirectUri = "http://localhost:3000";
  }
  if (window.location.hostname === "meqinsights.com") {
    // Ensure consistency with web URL to avoid ZITADEL errors
    redirectUri = "https://meqinsights.com";
  }
  return redirectUri;
};

export const getZitadelFromZitadelOrg = (zitadelOrg: ZitadelOrg) => {
  const config: ZitadelConfig = {
    authority: zitadelOrg?.authority,
    client_id: zitadelOrg?.reactClientId,
    redirect_uri:
      getZitadelRedirectUri(zitadelOrg!.reactRedirectUri) + "/callback",
    post_logout_redirect_uri: getZitadelRedirectUri(
      zitadelOrg!.reactRedirectUri,
    ),
  };
  const zitadel = createZitadelAuth(config) as unknown as ZitadelAuth;
  return zitadel;
};

const resetSessionAtoms = () => {
  queryClient.removeQueries({ queryKey: ACCESS_QUERY_KEY });
  store.set(selectedAccessRoleIdAtom, RESET);
  store.set(orgsAtom, RESET);
  store.set(userAtom, RESET);
  store.set(userConfigAtom, RESET);
  store.set(zitadelOrgAtom, RESET);
  store.set(workosAuthAtom, workosAuthInitialState);
};

// authkit-ui's /logout page clears the session cookies first-party with no
// CSRF token, so it is the fallback whenever /api/logout did not confirm the
// session ended — otherwise surviving cookies sign the user straight back in.
const endAuthkitSession = async (): Promise<boolean> => {
  const { csrfToken } = store.get(workosAuthAtom);
  if (!csrfToken) return false;
  try {
    await postLogout(csrfToken);
    return true;
  } catch (err) {
    console.warn("authkit-ui /api/logout failed", err);
    return false;
  }
};

const logoutWorkos = async () => {
  // Mock mode never has a real authkit-ui to call; skip the network round
  // trip and just clear local state. Matches the mock short-circuit in
  // PrivateRoutes.
  if (Env.VITE_MOCK === "true") {
    resetSessionAtoms();
    redirectToAuthkitLogin();
    return;
  }
  const isSessionEnded = await endAuthkitSession();
  resetSessionAtoms();
  if (isSessionEnded) {
    redirectToAuthkitLogin();
  } else {
    redirectToAuthkitLogout();
  }
};

export const logout = () => {
  if (isWorkOSAuth()) {
    void logoutWorkos();
    return;
  }
  const zitadelOrg = store.get(zitadelOrgAtom);
  if (zitadelOrg) {
    getZitadelFromZitadelOrg(zitadelOrg).signout();
  }
  resetSessionAtoms();
};

/**
 * Month-first date order. Brazil is deliberately absent: it grades on the USDA
 * scale but writes dates day-first.
 */
const COUNTRIES_USING_FORMAT_USA: ReadonlyArray<Country | undefined> = [
  Country.US,
];

export const getDateFormat = (
  org: Org,
  type:
    | "default"
    | "default_with_timezone"
    | "short"
    | "full"
    | "day_and_time"
    | "day_and_month"
    | "unix_timestamp",
) => {
  let dateFormat = DateFormat.DEFAULT as string;
  const isMonthFirst = COUNTRIES_USING_FORMAT_USA.includes(org?.country);

  switch (type) {
    case "default":
      dateFormat = isMonthFirst ? DateFormatUSA.DEFAULT : DateFormat.DEFAULT;
      break;
    case "default_with_timezone":
      dateFormat = isMonthFirst
        ? DateFormatUSA.DEFAULT_WITH_TIMEZONE
        : DateFormat.DEFAULT_WITH_TIMEZONE;
      break;
    case "short":
      dateFormat = isMonthFirst ? DateFormatUSA.SHORT : DateFormat.SHORT;
      break;
    case "full":
      dateFormat = isMonthFirst ? DateFormatUSA.FULL : DateFormat.FULL;
      break;
    case "day_and_time":
      dateFormat = isMonthFirst
        ? DateFormatUSA.DAY_AND_TIME
        : DateFormat.DAY_AND_TIME;
      break;
    case "day_and_month":
      dateFormat = isMonthFirst
        ? DateFormatUSA.DAY_AND_MONTH
        : DateFormat.DAY_AND_MONTH;
      break;
    case "unix_timestamp":
      dateFormat = isMonthFirst
        ? DateFormatUSA.UNIX_TIMESTAMP
        : DateFormat.UNIX_TIMESTAMP;
      break;
  }
  return dateFormat;
};

export const getOrgFromHostname = (hostname: string) => {
  let org = "";
  const orgString = hostname.split(".")[0];
  switch (orgString) {
    case "meqinsights":
    case "www":
    case "localhost":
    case "dev":
    case "alliance":
      org = "MEQ";
      break;
    case "gmp":
      org = "GMP";
      break;
    case "jbs":
      org = "JBS";
      break;
  }

  return org;
};

export const getOrgFromOrgIdString = (orgIdString: string) =>
  orgIdString ? orgIdString.split("_")[0] : "NotValidOrg";

// Lowercased org short name — the "org section" used in image-service URL paths.
// Centralises the `idString.split("_")[0].toLowerCase()` pattern that was
// re-implemented across the image components.
export const getOrgShortNameLower = (orgIdString?: string) =>
  getOrgFromOrgIdString(orgIdString ?? "").toLowerCase();

/** The site an idString names, absent for an org that carries none. */
export const getSiteFromOrgIdString = (
  idString?: string,
): string | undefined => (idString ? idString.split("_")[1] : undefined);

export const getLocationFromOrgIdString = (idString?: string) =>
  idString
    ? (getSiteFromOrgIdString(idString) ?? ALL_LOCATIONS)
    : NO_ORG_LOCATION;

export const getCameraOwnerStringFromOrgIdString = (idString?: string) =>
  idString ? idString.toLowerCase().replaceAll("_", "-") : "";

export const getJsDataTypeFromServerDataType = (serverDataType?: string) => {
  if (!serverDataType) {
    return undefined;
  }

  // Strip Nullable() wrapper if present
  const baseType = serverDataType.replace(/^Nullable\((.*)\)$/, "$1");

  let type: JsDataType = undefined;

  // Check pattern-based types first
  if (
    baseType.startsWith("Decimal(") ||
    baseType.startsWith("Float") ||
    baseType.startsWith("Int") ||
    baseType.startsWith("UInt")
  ) {
    return "number";
  }

  if (baseType.startsWith("DateTime")) {
    return "datetime";
  }

  if (baseType.startsWith("Date")) {
    return "date";
  }

  // Then handle exact matches
  switch (baseType) {
    case "text":
      type = "string";
      break;
    case "fixed":
    case "real":
      type = "number";
      break;
    case "date":
      type = "date";
      break;
    case "datetime":
    case "timestamp_ntz":
    case "timestamp_tz":
      type = "datetime";
      break;
  }

  return type;
};

export const applyUpperCaseTerms = (str: string) => {
  let updatedStr = str;
  UPPER_CASE_TERMS.forEach((i) => {
    updatedStr = updatedStr.replaceAll(i.toLowerCase(), i);
    updatedStr = updatedStr.replaceAll(startCase(i.toLowerCase()), i);
  });
  return updatedStr;
};

/**
 * Display label for a column key: `common:columnNames.<key>`, then the column
 * registry's fallback label, then a start-cased derivation of the key itself.
 *
 * v1 passes the raw result column name; the v2 endpoints pass the server's
 * `labelKey`, which already carries the preset's label overrides and therefore
 * differs from the data key at a US org.
 */
export const resolveColumnLabel = (key: string): string => {
  const translationKey = `columnNames.${key}`;
  const translated = i18n.t(translationKey, { ns: "common" });

  // i18next returns the key itself when no translation exists.
  if (translated !== translationKey) {
    return translated;
  }

  const labelFallback = getColumnLabelFallback(key);
  if (labelFallback !== undefined) {
    return labelFallback;
  }
  return applyUpperCaseTerms(
    startCase(key.toLowerCase()).replace(PERCENTAGE_OF, "%"),
  );
};

// transfer db data type to js data type
export const optimiseQueryResult = (queryResult?: QueryResult) => {
  if (queryResult) {
    const { columns, ...rest } = queryResult;
    return {
      columns:
        columns && columns.length > 0
          ? columns.map((i) => ({
              name: i.name,
              label: resolveColumnLabel(i.name),
              scale: i.scale,
              type: getJsDataTypeFromServerDataType(i.type),
            }))
          : [],
      ...rest,
    };
  }
  return undefined;
};

export const getDateRangeOptions = (optionKeys: readonly string[]) =>
  optionKeys.map((i) => {
    const n = Number(i.split("-")[0]);
    const u = i.split("-")[1] === "d" ? "days" : "months";
    const datePoint: { n: number; u: "days" | "months" } = { n, u };
    return {
      key: i,
      label: `${n} ${startCase(n === 1 ? u.substring(0, u.length - 1) : u)}`,
      value: datePoint,
    };
  });

export const showAllAttributesWhenHoverChart = ({
  item,
  includeAttributes,
  excludeAttributes = [],
}: {
  item: LooseObject;
  includeAttributes?: string[];
  excludeAttributes?: string[];
}) =>
  Object.keys(item)
    .filter((i) =>
      includeAttributes
        ? includeAttributes.includes(i)
        : !excludeAttributes.includes(i),
    )
    .sort()
    .map(
      (i) =>
        `${applyUpperCaseTerms(
          startCase(i.toLowerCase()).replace(PERCENTAGE_OF, "%"),
        )}: ${item[i]}`,
    )
    .join("<br />");

export const prepareQueryResultForTable = ({
  data,
  org,
  hiddenColumnNames,
  columnParams,
}: {
  data?: QueryResult;
  org: Org;
  hiddenColumnNames?: string[];
  columnParams?: LooseObject;
}): QueryResultTable => {
  data = optimiseQueryResult(data);
  const dateColumnNames = data?.columns
    ?.filter((i) => i.type === "date")
    .map((i) => i.name);
  const dateTimeColumnNames = data?.columns
    ?.filter((i) => i.type === "datetime")
    .map((i) => i.name);

  const tableColumns: MeqColumnDef<MeqRowData, unknown>[] =
    data?.columns
      ?.filter((i) => !hiddenColumnNames?.includes(i.name))
      ?.map((i) => {
        const accessors = dateColumnNames?.includes(i.name)
          ? {
              accessorFn: (row: MeqRowData) => row[i.name],
              Cell: ({ row }: { row: MeqRowData }) => {
                const value = row.original[i.name];
                return value
                  ? dayjs(value).format(getDateFormat(org, "short"))
                  : null;
              },
              id: `${i.name}`,
            }
          : dateTimeColumnNames?.includes(i.name)
            ? {
                accessorFn: (row: MeqRowData) => row[i.name],
                Cell: ({ row }: { row: MeqRowData }) => {
                  const value = row.original[i.name];
                  if (!value) return null;
                  // Moment's forgiving parser accepted values that don't
                  // exactly match DEFAULT_WITH_TIMEZONE (e.g. timestamp_ntz
                  // "YYYY-MM-DD HH:mm:ss.SSS"); dayjs's customParseFormat
                  // returns Invalid Date instead — fall back to the default
                  // parser to preserve the rendered output.
                  const strict = dayjs(
                    value,
                    DateFormatServer.DEFAULT_WITH_TIMEZONE,
                  );
                  return (strict.isValid() ? strict : dayjs(value)).format(
                    getDateFormat(org, "default"),
                  );
                },
                id: `${i.name}`,
              }
            : { accessorKey: i.name };
        return {
          ...accessors,
          header: i.label,
          ...(columnParams && Object.keys(columnParams).includes(i.name)
            ? columnParams[i.name]
            : {}),
        };
      }) || [];

  return { ...data, tableColumns, tableRows: data?.rows || [] };
};

export const objectWithKeySorted = (obj: LooseObject) => {
  const objWithKeySorted: LooseObject = {};
  const keys = Object.keys(obj).sort();
  for (let i = 0; i < keys.length; i++) {
    const value = obj[keys[i]];
    if (
      (value && JSON.stringify(value) !== JSON.stringify({})) ||
      value === 0
    ) {
      objWithKeySorted[keys[i]] = value;
    }
  }
  return objWithKeySorted;
};

// Deterministic sampling function using seeded random
export const sampleData = (
  data: LooseObject[] | undefined,
  size?: number,
): LooseObject[] => {
  if (!data) return [];

  const sampleSize = size || CHART_DATA_COUNT_THRESHHOLD;
  if (data.length <= sampleSize) return data;

  // Create seeded random number generator
  const rng = seedrandom("123");

  // Fisher-Yates shuffle with seeded random for consistent sampling
  const shuffled = [...data];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }

  return shuffled.slice(0, sampleSize);
};

export const optimisePageTitle = (title?: string) => {
  let optimisedTitle = title || "";
  WORDS_EXCLUDING_FROM_PAGE_TITLE.forEach(
    (i) => (optimisedTitle = optimisedTitle.replace(i, "").trim()),
  );
  return optimisedTitle;
};

export const getCsvConfig = (
  filename: string,
  columnHeaders?: Array<ColumnHeader> | undefined,
) => {
  return mkConfig({
    fieldSeparator: ",",
    decimalSeparator: ".",
    useKeysAsHeaders: !columnHeaders,
    filename,
    columnHeaders,
  });
};

export const isValidGmpGridPricingData = (
  gridPricingData: GridPricingDataType,
) => {
  let isValid = !!gridPricingData;
  gridPricingData.prices.forEach((i) => {
    if (!i.lmyCategory1 || !i.lmyCategory2) {
      isValid = false;
    }
  });
  if (gridPricingData.gridType === "GL") {
    gridPricingData.prices.forEach((i) => {
      if (!i.lmyCategory3 || !i.lmyCategory4 || !i.lmyCategory5) {
        isValid = false;
      }
    });
  }
  return isValid;
};

export const sortArrayByAnotherArray = <T extends Record<string, unknown>>(
  arrayToBeSorted: T[],
  key: string,
  arrayBy: (string | number)[],
) =>
  arrayToBeSorted.sort(
    (a, b) =>
      arrayBy.indexOf(a[key] as string | number) -
      arrayBy.indexOf(b[key] as string | number),
  );

export const getHchartPointFormatExtra = (
  attributes: QueryResultColumn[],
  showDashWhenEmpty = false,
) =>
  attributes.length > 0
    ? attributes
        .map((i) =>
          showDashWhenEmpty
            ? `<br/>${i.label}: <b>{#if point.${i.name}}{point.${i.name}}{else}-{/if}</b>`
            : `<br/>${i.label}: <b>{point.${i.name}}</b>`,
        )
        .join("")
    : "";

// Identifier columns shown in GMP scatter tooltips (PIC, Lot Reference, EID),
// with EID always ordered last so it appears at the bottom of every tooltip.
export const getGmpTooltipIdentifierColumns = (
  columns: QueryResultColumn[] | undefined,
) =>
  (columns ?? [])
    .filter((i) => ["PIC", "LOT_REFERENCE", "EID"].includes(i.name))
    .sort((a, b) => Number(a.name === "EID") - Number(b.name === "EID"));

export const inRangeInclusive = (
  x: number,
  range: [number, number],
): boolean => {
  return x >= range[0] && x <= range[1];
};

export const getUniqSortedAndGroupedSuppliers = (suppliers: LooseObject[]) =>
  orderBy(
    uniqBy(suppliers, "SUPPLIER_NO")
      .filter((i) => i.fullName && i.fullName !== "Other")
      .map((i) => ({
        label: i.SUPPLIER_NO?.toString(),
        value: i.SUPPLIER_NO,
        fullName: i.fullName,
      })) || [],
    ["fullName", "label"],
  ).concat(
    orderBy(
      uniqBy(suppliers, "SUPPLIER_NO")
        .filter((i) => !i.fullName || i.fullName === "Other")
        .map((i) => ({
          label: i.SUPPLIER_NO?.toString(),
          value: i.SUPPLIER_NO,
          fullName: "Other",
        })) || [],
      ["label"],
    ),
  );
